import { Grid } from '@mairistem/composants';
import {
	getErrorMessage,
	type UserRole,
	useEtiquette,
} from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import TypePeriode from '../../../../constants/simulation/typePeriode';
import type TreeRow from '../../../../interfaces/feuilleSaisie/treeRow';
import type MontantSimulation from '../../../../interfaces/simulation/montantSimulation';
import {
	updateMontant,
	upsertDemandeMontant,
	upsertMontant,
} from '../../../../services/simulation/montantSimulation';
import MontantsSimulationModalContentHeader from './MontantsSimulationModalContentHeader';
import MontantsSimulationModalContentItem from './MontantsSimulationModalContentItem';

interface MontantsSimulationModalContentProps {
	identifiantEntite: number;
	identifiantSimulation: number;
	montantsSimulation: MontantSimulation[];
	onAddMontant: () => void;
	onChangeCommentaire: (e: React.FocusEvent<HTMLInputElement>) => void;
	onChangeMontant: (
		differenceMontant: number,
		typePeriode: TypePeriode,
	) => void;
	rowData: TreeRow;
	setMontantsSimulation: React.Dispatch<
		React.SetStateAction<MontantSimulation[]>
	>;
	transfertCpta: boolean;
	typePeriode: TypePeriode;
	userRole: UserRole;
}

const MontantsSimulationModalContent = ({
	identifiantEntite,
	identifiantSimulation,
	montantsSimulation,
	onAddMontant,
	onChangeCommentaire,
	onChangeMontant,
	rowData,
	setMontantsSimulation,
	transfertCpta,
	typePeriode,
	userRole,
}: MontantsSimulationModalContentProps) => {
	const [loadingCreation, setLoadingCreation] = React.useState(false);

	const EtiquettesInput = useEtiquette(
		'/api/finances/simulation/etiquettes',
		identifiantEntite,
	);

	const handleUpsertMontant = async () => {
		const data = {
			identifiantAnalytique: rowData?.idcm_analytique ?? null,
			identifiantArticle: rowData?.idcm_article,
			identifiantChapitre: rowData?.idcm_chapitre,
			identifiantFonction: rowData?.idcm_fonction,
			identifiantOperation: rowData?.idcm_operation,
			identifiantSimulation,
			identifiantVentilation: rowData?.idcm_ventilation ?? null,
			onlyCreate: true,
			sens: rowData?.imp_sens,
		};

		let montantSimulation = {} as MontantSimulation;

		// Si on est en demande, on fait une requete sur une autre route accessible par les gestionnaires
		if (typePeriode === TypePeriode.DEMANDE) {
			montantSimulation = await upsertDemandeMontant(data);
		} else {
			montantSimulation = await upsertMontant(data);
		}

		return montantSimulation;
	};

	const handleAddMontant = async () => {
		try {
			setLoadingCreation(true);
			const montantSimulation = await handleUpsertMontant();
			setMontantsSimulation((old) => [...old, montantSimulation]);
			// On incremente le nombre de montant dans la rowData
			onAddMontant();
		} catch (e) {
			toast.error(getErrorMessage(e, 'Erreur lors de la création du montant'));
		} finally {
			setLoadingCreation(false);
		}
	};

	const handleMontantSimulationChange = async (
		montantSimulation: Partial<MontantSimulation>,
	) => {
		try {
			const data = await updateMontant(
				montantSimulation,
				new URLSearchParams({ withEtiquettes: 'true' }),
			);
			setMontantsSimulation((old) =>
				old.map((montant) =>
					montant.identifiant === montantSimulation.identifiant
						? data
						: montant,
				),
			);
		} catch (e) {
			toast.error(
				getErrorMessage(e, 'Erreur lors de la mise à jour du montant'),
			);
		}
	};

	return (
		<Grid>
			<MontantsSimulationModalContentHeader
				loading={loadingCreation}
				montantsSimulation={montantsSimulation}
				onAddMontant={handleAddMontant}
				transfertCpta={transfertCpta}
				typePeriode={typePeriode}
				userRole={userRole}
			/>
			{montantsSimulation.map((montantSimulation) => (
				<MontantsSimulationModalContentItem
					EtiquettesInput={EtiquettesInput}
					key={montantSimulation.identifiant}
					montantSimulation={montantSimulation}
					onChange={(data: Partial<MontantSimulation>) =>
						handleMontantSimulationChange({
							...data,
							identifiant: montantSimulation.identifiant,
						})
					}
					onChangeCommentaire={onChangeCommentaire}
					onChangeMontant={onChangeMontant}
					transfertCpta={transfertCpta}
					typePeriode={typePeriode}
					userRole={userRole}
				/>
			))}
		</Grid>
	);
};

export default MontantsSimulationModalContent;
